import React from 'react';
import { createContext, useContext, useEffect, useState } from "react";
var _hasLocalStorage = undefined;
export function hasLocalStorage() {
    if (typeof _hasLocalStorage === 'undefined') {
        _hasLocalStorage = (() => {
            var test = "delayedstorage";
            try {
                localStorage.setItem(test, test);
                localStorage.removeItem(test);
            }
            catch (e) {
                return false;
            }
            return true;
        })();
    }
    return _hasLocalStorage;
}
export function getLocalStorage(key) {
    try {
        return JSON.parse(localStorage.getItem(key));
    }
    catch (e) {
        return {};
    }
}
export function setLocalStorage(key, entry) {
    try {
        const existing = getLocalStorage(key);
        const updated = { ...existing, ...entry };
        localStorage.setItem(key, JSON.stringify(updated));
    }
    catch (e) {
    }
}
export function obfuscateString(value, key) {
    const keyBytes = Array.from(key).map((c) => c.charCodeAt(0) & 0xff);
    const valBytes = Array.from(value).map((c) => c.charCodeAt(0) & 0xff);
    const out = valBytes.map((b, i) => b ^ keyBytes[i % keyBytes.length]);
    return btoa(String.fromCharCode(...out));
}
export function deobfuscateString(value, key) {
    try {
        const keyBytes = Array.from(key).map((c) => c.charCodeAt(0) & 0xff);
        const raw = atob(value);
        const bytes = Array.from(raw).map((c) => c.charCodeAt(0) & 0xff);
        const out = bytes.map((b, i) => b ^ keyBytes[i % keyBytes.length]);
        return String.fromCharCode(...out);
    }
    catch {
        return null;
    }
}
let _hasSessionStorage = undefined;
export function hasSessionStorage() {
    if (typeof _hasSessionStorage === "undefined") {
        _hasSessionStorage = (() => {
            const test = "delayedsession";
            try {
                sessionStorage.setItem(test, test);
                sessionStorage.removeItem(test);
                return true;
            }
            catch {
                return false;
            }
        })();
    }
    return _hasSessionStorage;
}
export function getSessionStorage(key) {
    if (!hasSessionStorage())
        return undefined;
    try {
        const raw = sessionStorage.getItem(key);
        return raw ? JSON.parse(raw) : undefined;
    }
    catch {
        return undefined;
    }
}
export function setSessionStorage(key, value) {
    if (!hasSessionStorage())
        return;
    try {
        sessionStorage.setItem(key, JSON.stringify(value));
    }
    catch {
        // ignore
    }
}
/**
 * Clear all sessionStorage keys that start with the given prefix. Useful for
 * removing cached, device-local secrets such as judge PINs on logout.
 */
export function clearSessionStorageByPrefix(prefix) {
    if (!hasSessionStorage())
        return;
    try {
        const keysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith(prefix)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    }
    catch {
        // ignore cache clear errors
    }
}
const defaultUserSettings = {
    theme: 'dark',
    switchSide: false,
    managerLeftSiderCollapsed: false,
    managerRightSiderCollapsed: false
};
// Create the context
const UserSettingsContext = createContext(undefined);
// Hook to use the UserSettingsContext
export const useUserSettings = () => {
    const context = useContext(UserSettingsContext);
    if (!context) {
        throw new Error('useUserSettings must be used within a UserSettingsProvider');
    }
    return context;
};
// UserSettingsProvider component
export const UserSettingsProvider = ({ children }) => {
    const [settings, setSettings] = useState(() => {
        if (hasLocalStorage()) {
            const savedSettings = localStorage.getItem('userSettings');
            return savedSettings ? JSON.parse(savedSettings) : defaultUserSettings;
        }
        return defaultUserSettings;
    });
    useEffect(() => {
        if (hasLocalStorage()) {
            localStorage.setItem('userSettings', JSON.stringify(settings));
        }
    }, [settings]);
    const updateSettings = (updates) => {
        setSettings(prev => {
            const newSettings = { ...prev, ...updates };
            if (hasLocalStorage()) {
                setLocalStorage('userSettings', updates);
            }
            return newSettings;
        });
    };
    return (React.createElement(UserSettingsContext.Provider, { value: { settings, updateSettings } }, children));
};
